import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, Text } from '@fragments';
import { IconBlur, IconEye, IconEyeOff, IconInnerShadowTopLeft, IconMinus, IconPlus, IconShadow, IconSunHigh, IconTexture, IconWindow } from '@tabler/icons-react';
import SolidPicker from '../../ColorPicker/SolidPicker';
import { EFFECT_TYPES, LIMITS, createEffect, normalizeColor } from '../../../../model/schema';
import { withAlpha } from '../../../../model/colors';

const ICONS = { drop_shadow: IconShadow, inner_shadow: IconInnerShadowTopLeft, glass: IconWindow, layer_blur: IconBlur, texture: IconTexture };
const SHADOWS = ['drop_shadow', 'inner_shadow'];

// A number typed or scrubbed: drag the label sideways to change it (Shift ×10), arrows
// step it, Enter or leaving the field applies what was typed.
function NumberInput({ label, prefix, value, onChange, min, max, step = 1, suffix, scale = 1 }) {
  const shown = Math.round(value * scale * 100) / 100;
  const [text, setText] = useState(String(shown));
  useEffect(() => { setText(String(shown)); }, [shown]);
  const clamp = next => Math.min(max, Math.max(min, next));
  const emit = next => onChange(clamp(Math.round(next / step) * step) / scale);
  const scrub = event => {
    if (event.button !== 0) return;
    event.preventDefault();
    const start = event.clientX;
    const from = value * scale;
    const element = event.currentTarget;
    element.setPointerCapture?.(event.pointerId);
    const move = next => emit(from + Math.round((next.clientX - start) / 2) * step * (next.shiftKey ? 10 : 1));
    const stop = () => { element.removeEventListener('pointermove', move); element.removeEventListener('pointerup', stop); element.removeEventListener('pointercancel', stop); };
    element.addEventListener('pointermove', move);
    element.addEventListener('pointerup', stop);
    element.addEventListener('pointercancel', stop);
  };
  return <label className="art-fill__field art-effect__number">
    <span className="art-effect__prefix" onPointerDown={scrub} title={label}>{prefix}</span>
    <input value={text} aria-label={label} inputMode="decimal" spellCheck={false}
      onChange={event => setText(event.target.value)}
      onBlur={() => { const next = Number(text.replace(',', '.')); if (Number.isFinite(next)) emit(next); else setText(String(shown)); }}
      onKeyDown={event => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); emit(value * scale + (event.key === 'ArrowUp' ? 1 : -1) * step * (event.shiftKey ? 10 : 1)); }
      }} />
    {suffix && <span aria-hidden="true">{suffix}</span>}
  </label>;
}

function Row({ label, children }) {
  return <div className="art-effect__row"><Text.caption contrast={6}>{label}</Text.caption><div className="art-effect__fields">{children}</div></div>;
}

// A shadow's color: the swatch opens the picker inside the box, beside the hex and opacity.
function ColorRow({ effect, onPatch, colors }) {
  const { t } = useTranslation('artEditor');
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(effect.color.slice(1).toUpperCase());
  useEffect(() => { setHex(effect.color.slice(1).toUpperCase()); }, [effect.color]);
  return <>
    <Row label={t('effectColor')}>
      <div className="art-fill__field art-effect__color">
        <button type="button" className="art-effect__swatch" aria-label={t('effectColor')} aria-expanded={open} onClick={() => setOpen(value => !value)}>
          <i style={{ background: withAlpha(effect.color, effect.opacity) }} />
        </button>
        <input value={hex} aria-label="HEX" spellCheck={false} onChange={event => setHex(event.target.value)}
          onBlur={() => { const color = normalizeColor(`#${hex.replace('#', '')}`, null); if (color) onPatch({ color }, 'color'); else setHex(effect.color.slice(1).toUpperCase()); }}
          onKeyDown={event => { if (event.key === 'Enter') event.currentTarget.blur(); }} />
      </div>
      <NumberInput label={t('opacity')} prefix="%" value={effect.opacity} scale={100} min={0} max={100} onChange={opacity => onPatch({ opacity }, 'opacity')} />
    </Row>
    {open && <div className="art-effect__picker">
      <SolidPicker color={effect.color} alpha={effect.opacity} showAlpha brand={colors.brand} used={colors.used} onChange={({ color, alpha }) => onPatch({ color, opacity: alpha }, 'color')} />
    </div>}
  </>;
}

// One effect as a box (Figma's effect rows): its kind (switching keeps what a shadow had),
// shown or hidden, removed; then the settings of that kind.
function EffectBox({ effect, onPatch, onRetype, onRemove, colors }) {
  const { t } = useTranslation('artEditor');
  const Icon = ICONS[effect.type];
  const shadow = SHADOWS.includes(effect.type);
  return <section className={`art-effect ${effect.visible ? '' : 'is-hidden'}`} aria-label={t(`effect_${effect.type}`)}>
    <header className="art-effect__head">
      <Menu placement="bottom-start" items={EFFECT_TYPES.map(type => { const TypeIcon = ICONS[type]; return { label: t(`effect_${type}`), icon: <TypeIcon />, active: type === effect.type, onClick: () => onRetype(type) }; })}>
        <Button size="small" variant="text" color="overlay" dropdown preventLoading startIcon={<Icon />} aria-label={`${t('effectType')}: ${t(`effect_${effect.type}`)}`}>{t(`effect_${effect.type}`)}</Button>
      </Menu>
      <span className="art-fill__spacer" />
      <Button size="small" variant="text" color="overlay" preventLoading icon={effect.visible ? <IconEye /> : <IconEyeOff />} aria-pressed={!effect.visible}
        aria-label={t(effect.visible ? 'effectHide' : 'effectShow')} tooltip={t(effect.visible ? 'effectHide' : 'effectShow')} onClick={() => onPatch({ visible: !effect.visible })} />
      <Button size="small" variant="text" color="overlay" preventLoading icon={<IconMinus />} aria-label={t('effectRemove')} tooltip={t('effectRemove')} onClick={onRemove} />
    </header>
    <div className="art-effect__body" inert={effect.visible ? undefined : ''}>
      {shadow && <>
        <Row label={t('effectPosition')}>
          <NumberInput label="X" prefix="X" value={effect.x} min={LIMITS.shadowOffset[0]} max={LIMITS.shadowOffset[1]} onChange={x => onPatch({ x }, 'x')} />
          <NumberInput label="Y" prefix="Y" value={effect.y} min={LIMITS.shadowOffset[0]} max={LIMITS.shadowOffset[1]} onChange={y => onPatch({ y }, 'y')} />
        </Row>
        <Row label={t('blur')}>
          <NumberInput label={t('blur')} prefix={<IconBlur aria-hidden="true" />} value={effect.blur} min={LIMITS.effectBlur[0]} max={LIMITS.effectBlur[1]} onChange={blur => onPatch({ blur }, 'blur')} />
        </Row>
        <Row label={t('effectSpread')}>
          <NumberInput label={t('effectSpread')} prefix={<IconSunHigh aria-hidden="true" />} value={effect.spread} min={LIMITS.spread[0]} max={LIMITS.spread[1]} onChange={spread => onPatch({ spread }, 'spread')} />
        </Row>
        <ColorRow effect={effect} onPatch={onPatch} colors={colors} />
        {effect.type === 'drop_shadow' && <label className="art-effect__check">
          <input type="checkbox" checked={effect.behind} onChange={event => onPatch({ behind: event.target.checked })} />
          <Text.caption contrast={8}>{t('effectBehind')}</Text.caption>
        </label>}
      </>}
      {effect.type === 'glass' && <>
        <Row label={t('glassBlur')}>
          <NumberInput label={t('glassBlur')} prefix={<IconBlur aria-hidden="true" />} value={effect.blur} min={LIMITS.effectBlur[0]} max={LIMITS.effectBlur[1]} onChange={blur => onPatch({ blur }, 'blur')} />
        </Row>
        <Row label={t('glassOpacity')}>
          <NumberInput label={t('glassOpacity')} prefix="%" value={effect.opacity} scale={100} min={0} max={100} onChange={opacity => onPatch({ opacity }, 'opacity')} />
        </Row>
        <Row label={t('glassEdge')}>
          <NumberInput label={t('glassEdge')} prefix="%" value={effect.edge} scale={100} min={0} max={100} onChange={edge => onPatch({ edge }, 'edge')} />
        </Row>
      </>}
      {effect.type === 'layer_blur' && <Row label={t('blur')}>
        <NumberInput label={t('blur')} prefix={<IconBlur aria-hidden="true" />} value={effect.radius} min={LIMITS.effectBlur[0]} max={LIMITS.effectBlur[1]} onChange={radius => onPatch({ radius }, 'radius')} />
      </Row>}
      {effect.type === 'texture' && <>
        <Row label={t('textureAmount')}>
          <NumberInput label={t('textureAmount')} prefix="%" value={effect.amount} scale={100} min={0} max={100} onChange={amount => onPatch({ amount }, 'amount')} />
        </Row>
        <Row label={t('textureSize')}>
          <NumberInput label={t('textureSize')} prefix={<IconTexture aria-hidden="true" />} value={effect.size} min={1} max={8} step={0.5} onChange={size => onPatch({ size }, 'size')} />
        </Row>
      </>}
    </div>
  </section>;
}

// The layer's effects, stacked (painted in this order): up to 8, any kind, several shadows
// included. Every change is one undo step per field (typing and scrubbing fold together).
export default function EffectsPanel({ layer, update, colors }) {
  const { t } = useTranslation('artEditor');
  const effects = layer.effects || [];
  const set = (next, key) => update({ effects: next }, key ? { key: `${layer.id}:effects:${key}` } : { key: null });
  const patchAt = index => (change, field) => set(effects.map((effect, at) => (at === index ? { ...effect, ...change } : effect)), field ? `${index}:${field}` : null);
  // Switching between the two shadows keeps their settings; any other kind starts fresh.
  const retype = index => type => set(effects.map((effect, at) => {
    if (at !== index || effect.type === type) return effect;
    if (SHADOWS.includes(effect.type) && SHADOWS.includes(type)) { const { behind, ...rest } = effect; return type === 'drop_shadow' ? { ...rest, type, behind: false } : { ...rest, type }; }
    return { ...createEffect(type), visible: effect.visible };
  }));
  const add = type => set([...effects, createEffect(type)]);
  return <div className="art-effects">
    {!effects.length && <Text.caption contrast={6}>{t('effectsEmpty')}</Text.caption>}
    {effects.map((effect, index) => <EffectBox key={index} effect={effect} colors={colors} onPatch={patchAt(index)} onRetype={retype(index)}
      onRemove={() => set(effects.filter((_, at) => at !== index))} />)}
    <Menu placement="bottom-start" items={EFFECT_TYPES.map(type => { const TypeIcon = ICONS[type]; return { label: t(`effect_${type}`), icon: <TypeIcon />, onClick: () => add(type) }; })}>
      <Button className="art-effects__add" size="small" variant="dashed" color="overlay" preventLoading startIcon={<IconPlus />} disabled={effects.length >= LIMITS.effects}>{t('addEffect')}</Button>
    </Menu>
  </div>;
}
